import type { CartInfo, CartInfoItem, CartInfoParams } from "@api/getCartInfo";
import type { AccessoryProduct, ShipmentRate, ShippingAddressType } from "@constants/formDefaults";
import { useToken } from "@contexts/TokenContext";
import { hasPhysicalProducts } from "@hooks/useStepFlow";
import { useAuthUpdatePurchase } from "@mutations/useAuthUpdatePurchase";
import { useUpdatePurchase } from "@mutations/useUpdatePurchase";
import { useGetCartInfo } from "@queries/useGetCartInfo";
import { useGetLicenseProducts } from "@queries/useGetLicenseProducts";
import type { SupportedCurrency } from "@utils/currencyUtils";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";

const getDefaultCartInfo = (currency: SupportedCurrency): CartInfo => ({
  currency,
  subtotal: 0,
  total_amount: 0,
  applied_discounts: [],
  tax: undefined,
  items: [],
  bundles: [],
});

export interface LineItem {
  sku: string;
  title: string;
  description: string;
  price: number;
  quantity: number;
  frequency?: string;
}

export interface CartInfoContextValue {
  lineItems: LineItem[];
  subtotal: number;
  cartInfo: CartInfo;
  isLoading: boolean;
  isCartInfoLoading: boolean;
  isTaxLoading: boolean;
  hasBillingPostalCode: boolean;
  error: Error | null;
  setServerCartInfo: (data: CartInfo) => void;
  resetForConfirmPage: () => void;
  licenseProducts: LineItem[];
  isLoadingLicenseProducts: boolean;
  selectedCurrency: SupportedCurrency;
  pricingData: Map<string, CartInfoItem>;
}

const CartInfoContext = createContext<CartInfoContextValue | undefined>(undefined);

interface CartInfoProviderProps {
  children: ReactNode;
}

export function CartInfoProvider({ children }: CartInfoProviderProps) {
  const { control, getValues, setValue } = useFormContext();
  const { isAuthenticated, idToken } = useToken();

  // State
  const [serverCartInfo, setServerCartInfo] = useState<CartInfo | null>(null);
  const [isTaxLoading, setIsTaxLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pricingData, setPricingData] = useState<Map<string, CartInfoItem>>(new Map());

  // Refs
  const initialFetchDoneRef = useRef(false);
  const prevStripePaymentMethodIdRef = useRef<string | null>(null);

  // Reset function for Confirm page - clears server data to show optimistic view
  // and allows billingPostalCode to trigger fresh updatePurchase
  const resetForConfirmPage = useCallback(() => {
    setServerCartInfo(null);
    setError(null);
    setValue("billingPostalCode", "");
    setValue("stripePaymentMethodId", "");
    prevStripePaymentMethodIdRef.current = null;
  }, [setValue]);

  // Watch only the essential fields for triggering updates
  const items: Array<{ sku: string; quantity?: number }> =
    useWatch({ control, name: "items" }) || [];
  const selectedCurrency =
    (useWatch({ control, name: "selectedCurrency" }) as SupportedCurrency) || "usd";

  const billingPostalCode: string | undefined = useWatch({ control, name: "billingPostalCode" });
  const uuid: string | undefined = useWatch({ control, name: "uuid" });
  const formStripePaymentMethodId: string | undefined = useWatch({
    control,
    name: "stripePaymentMethodId",
  });

  // Watch fields for optimistic display (not for triggering)
  const accessories: Record<string, number> = useWatch({ control, name: "accessories" }) || {};
  const accessoryProducts: AccessoryProduct[] =
    useWatch({ control, name: "accessoryProducts" }) || [];
  const shipmentRate: ShipmentRate | undefined = useWatch({ control, name: "shipmentRate" });

  // Lazy fetch license products - only when Licenses page sets SKUs to fetch
  const licenseSKUsToFetch: string[] = useWatch({ control, name: "licenseSKUsToFetch" }) || [];
  const { data: licenseProducts = [], isLoading: isLoadingLicenseProducts } = useGetLicenseProducts(
    licenseSKUsToFetch,
    selectedCurrency,
  );

  // Query for initial cart info (before uuid exists)
  const [queryParams, setQueryParams] = useState<CartInfoParams | null>(null);
  const {
    data: cartInfoData,
    isPending: isCartInfoPending,
    error: cartInfoError,
  } = useGetCartInfo(queryParams, idToken);

  // Mutation hooks
  const { mutateAsync: updatePurchase, isPending: isUpdatingPurchase } = useUpdatePurchase();
  const { mutateAsync: authUpdatePurchase, isPending: isAuthUpdatingPurchase } =
    useAuthUpdatePurchase();

  // Derive hasShippingStep locally
  const hasShippingStep = useMemo(() => {
    const mappedItems = items.map((item) => ({
      sku: item.sku,
      quantity: item.quantity || 1,
    }));
    return Object.keys(accessories).length > 0 || hasPhysicalProducts(mappedItems);
  }, [accessories, items]);

  // Loading states
  const isCartInfoLoading = queryParams !== null && isCartInfoPending;
  const isLoading = isCartInfoLoading || isUpdatingPurchase || isAuthUpdatingPurchase;

  // Phase 1: Initial fetch with getCartInfo (once on mount when items > 0)
  useEffect(() => {
    if (initialFetchDoneRef.current || items.length === 0) {
      return;
    }

    initialFetchDoneRef.current = true;

    const mappedItems = items.map((item) => ({
      sku: item.sku,
      quantity: item.quantity || 1,
    }));

    setQueryParams({
      items: mappedItems,
      purchase_type: "new_purchase",
      currency: selectedCurrency,
    });
  }, [items, selectedCurrency]);

  // Handle initial query data - populate both serverCartInfo and pricingData
  useEffect(() => {
    if (cartInfoData?.data) {
      // Normalize initial tax to undefined so UI doesn't show 0 before Stripe tax runs
      const normalized = {
        ...cartInfoData.data,
        tax: undefined,
        items: cartInfoData.data.items ?? [],
        bundles: cartInfoData.data.bundles ?? [],
      };
      setServerCartInfo(normalized);
      setError(null);

      // Populate pricingData from the response
      const allItems = [...(cartInfoData.data.items ?? []), ...(cartInfoData.data.bundles ?? [])];
      setPricingData((prev) => {
        const next = new Map(prev);
        allItems.forEach((item) => {
          next.set(item.sku, item);
        });
        return next;
      });

      // Extract categories from API response (single source of truth)
      const productCatSet = new Set<string>();
      const accessoryCatSet = new Set<string>();
      for (const item of allItems) {
        item.product_categories?.forEach((c: string) => {
          productCatSet.add(c);
        });
        item.accessory_categories?.forEach((c: string) => {
          accessoryCatSet.add(c);
        });
      }
      setValue("productCategories", Array.from(productCatSet));
      setValue("accessoryCategories", Array.from(accessoryCatSet));
    }
  }, [cartInfoData, setValue]);

  // Handle query errors
  useEffect(() => {
    if (cartInfoError) {
      setError(cartInfoError);
      console.error("CartInfoContext query error:", cartInfoError);
    }
  }, [cartInfoError]);

  // Phase 2: Update purchase when stripePaymentMethodId changes (and uuid exists)
  useEffect(() => {
    // Skip if no uuid (purchase not created yet)
    if (!uuid) {
      return;
    }

    // Only proceed when the payment method id changes
    const pmChanged = (formStripePaymentMethodId || null) !== prevStripePaymentMethodIdRef.current;
    if (!pmChanged) {
      return;
    }

    prevStripePaymentMethodIdRef.current = formStripePaymentMethodId || null;
    setIsTaxLoading(true);
    setError(null);

    const callUpdatePurchase = async () => {
      try {
        // Gather ALL current form values at call time
        const formValues = getValues();

        const mappedItems = items.map((item) => ({
          sku: item.sku,
          quantity: item.quantity || 1,
        }));

        // Build shipping address if applicable
        const shippingAddress: ShippingAddressType | undefined = hasShippingStep
          ? {
              address1: formValues.address1 || "",
              address2: formValues.address2 || "",
              city: formValues.city || "",
              state: formValues.state || "",
              country: formValues.country || "",
              postal_code: formValues.postalCode || "",
            }
          : undefined;

        // Build the full payload
        const payload = {
          items: mappedItems,
          billing_address: { postal_code: billingPostalCode },
          ...(formStripePaymentMethodId && {
            stripe_payment_method_id: formStripePaymentMethodId,
          }),
          ...(hasShippingStep &&
            shipmentRate && {
              shipping_address: shippingAddress,
              shipment_rate: shipmentRate,
            }),
          ...(formValues.professionalCredentials && {
            professional_credentials: formValues.professionalCredentials,
          }),
          ...(formValues.couponId && { coupon_id: formValues.couponId }),
        };

        if (isAuthenticated) {
          const response = await authUpdatePurchase({ uuid, params: payload });
          if (response?.cart_info) {
            setServerCartInfo(response.cart_info);
          }
          if (response?.updateError) {
            // TODO: Should we handle update error?
            console.warn("Update purchase warning:", response.updateError);
          }
        } else {
          const result = await updatePurchase({ uuid, ...payload });
          if (result.data?.cart_info) {
            setServerCartInfo(result.data.cart_info);
          }
          if (result.data?.updateError) {
            // TODO: Should we handle update error?
            console.warn("Update purchase warning:", result.data.updateError);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to update cart info"));
        console.error("CartInfoContext updatePurchase error:", err);
      } finally {
        setIsTaxLoading(false);
      }
    };

    callUpdatePurchase();
  }, [
    uuid,
    formStripePaymentMethodId,
    isAuthenticated,
    items,
    hasShippingStep,
    shipmentRate,
    getValues,
    updatePurchase,
    authUpdatePurchase,
    billingPostalCode,
  ]);

  // Calculate PURELY OPTIMISTIC line items from form state only
  // No dependency on serverCartInfo - prevents duplicate items issue
  const lineItems = useMemo<LineItem[]>(() => {
    const allLineItems: LineItem[] = [];

    // 1. Main product items - lookup from pricingData (getCartInfo response)
    items.forEach((item) => {
      // Skip if this is an accessory (exists in accessories object with qty > 0)
      if (accessories[item.sku] > 0) return;
      // Skip if this is a license (exists in licenseProducts)
      if (licenseProducts.some((lp) => lp.sku === item.sku)) return;

      const pricing = pricingData.get(item.sku);
      if (pricing) {
        allLineItems.push({
          sku: item.sku,
          title: pricing.product_title,
          description: pricing.product_description,
          price: pricing.unit_amount,
          quantity: item.quantity || 1,
          frequency: pricing.frequency,
        });
      }
    });

    // 2. Accessory items from form accessories + accessoryProducts
    Object.entries(accessories)
      .filter(([_, quantity]) => quantity > 0)
      .forEach(([sku, quantity]) => {
        const accessory = accessoryProducts.find((p) => p.SKU === sku);
        if (accessory) {
          allLineItems.push({
            sku,
            title: accessory.Name,
            description: accessory.ShortDescription || "Add-on item",
            price: accessory.PriceTier1,
            quantity,
          });
        }
      });

    // 3. License items - lookup from licenseProducts (useGetLicenseProducts response)
    items.forEach((item) => {
      const license = licenseProducts.find((lp) => lp.sku === item.sku);
      if (license) {
        allLineItems.push({
          sku: item.sku,
          title: license.title,
          description: license.description || "",
          price: license.price,
          quantity: item.quantity || 1,
        });
      }
    });

    // 4. Shipping - single source from form state only (prevents duplicates)
    if (shipmentRate) {
      allLineItems.push({
        sku: shipmentRate.serviceCode,
        title: `Shipping - ${shipmentRate.serviceName}`,
        description: "",
        price: shipmentRate.totalCost,
        quantity: 1,
      });
    }

    return allLineItems;
  }, [items, accessories, accessoryProducts, licenseProducts, shipmentRate, pricingData]);

  // Calculate optimistic subtotal
  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [lineItems]);

  const hasBillingPostalCode = Boolean(billingPostalCode);

  const value = useMemo(
    () => ({
      lineItems,
      subtotal,
      // serverCartInfo only used for tax, total_amount, discounts
      cartInfo: serverCartInfo ?? getDefaultCartInfo(selectedCurrency),
      isLoading,
      isCartInfoLoading,
      isTaxLoading,
      hasBillingPostalCode,
      error,
      setServerCartInfo,
      resetForConfirmPage,
      licenseProducts,
      isLoadingLicenseProducts,
      selectedCurrency,
      pricingData,
    }),
    [
      lineItems,
      subtotal,
      serverCartInfo,
      isLoading,
      isCartInfoLoading,
      isTaxLoading,
      hasBillingPostalCode,
      error,
      resetForConfirmPage,
      licenseProducts,
      isLoadingLicenseProducts,
      selectedCurrency,
      pricingData,
    ],
  );

  return <CartInfoContext.Provider value={value}>{children}</CartInfoContext.Provider>;
}

export function useCartInfo(): CartInfoContextValue {
  const context = useContext(CartInfoContext);
  if (context === undefined) {
    throw new Error("useCartInfo must be used within a CartInfoProvider");
  }
  return context;
}
